import { useMutation } from '@apollo/client';
import { Stack } from '@mui/material';
import { Button, Placeholder } from '@telegram-apps/telegram-ui';
import { initData, useSignal } from '@tma.js/sdk-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { VerifyTelegramAuthMutation, VerifyTelegramAuthMutationVariables } from '../client/generated/graphql';
import { verifyTelegramAuthMutation } from '../client/mutations/verifyTelegramAuth';
import { CommonIcon } from '../components/common/CommonIcon';
import { Page } from '../components/Page';

export const OAuthApprovalPage = () => {
    const { sessionId, clientId } = useParams<{ sessionId: string; clientId: string }>();
    const clientName = clientId ? clientId.charAt(0).toUpperCase() + clientId.slice(1) : 'Raycast';
    const initDataRaw = useSignal(initData.raw);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const [verifyTelegramAuth, { loading }] = useMutation<
        VerifyTelegramAuthMutation,
        VerifyTelegramAuthMutationVariables
    >(verifyTelegramAuthMutation);

    const handleApprove = async () => {
        if (!sessionId) return;
        if (!initDataRaw) {
            setStatus('error');
            setErrorMessage('Telegram initialization data not available');
            return;
        }
        try {
            const result = await verifyTelegramAuth({ variables: { sessionId, initDataRaw } });
            if (result.data?.verifyTelegramAuth) {
                setStatus('success');
            } else {
                throw new Error('Verification failed');
            }
        } catch (err) {
            setStatus('error');
            setErrorMessage(err instanceof Error ? err.message : String(err));
        }
    };

    const icon = (
        <CommonIcon icon="all_inclusive" type="filled" size={80} color="var(--tgui--text_color)" />
    );

    return (
        <Page back={false}>
            <Stack flex={1} alignItems="center" justifyContent="center">
                {status === 'success' && (
                    <Placeholder
                        header="Connected!"
                        description={`FocusLee is now connected to ${clientName}. You can close this window.`}
                    >
                        {icon}
                    </Placeholder>
                )}

                {status === 'error' && (
                    <Placeholder
                        header="Connection failed"
                        description={errorMessage || 'Something went wrong. Please try again.'}
                        action={<Button onClick={handleApprove}>Try again</Button>}
                    >
                        {icon}
                    </Placeholder>
                )}

                {status === 'idle' && (
                    <Placeholder
                        header={`Connect FocusLee to ${clientName}`}
                        description={`Allow ${clientName} to access your FocusLee account.`}
                        action={
                            <Button size="l" onClick={handleApprove} disabled={loading}>
                                {loading ? 'Connecting…' : 'Approve'}
                            </Button>
                        }
                    >
                        {icon}
                    </Placeholder>
                )}
            </Stack>
        </Page>
    );
};
